import * as React from "react";
import * as lodash from "lodash";
import HumanJson from "../human";

const { Component } = React;

import { Button, FormGroup, Input, Label, Modal } from "../components/Modal";
import { Router, Route, Link, useNavigate } from "react-router-dom";
import * as AccountStore from "../account/AccountStore";
import axios from "../general/HttpClient";
import Avatar from "react-avatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ProfileState {
    currentUser: any;
    showResendConfirmationModal: boolean;
    userUpdated: boolean;
    user: any;
    showUserUpdatedMessage: boolean;
    sharedKey: string;
    authenticatorUri: string;
    humanJson: any;
}

const Profile = () => {
    const [currentUser, setCurrentUser] = React.useState({} as AccountStore.Account);
    const [showResendConfirmationModal, setShowResendConfirmationModal] = React.useState(false);
    const [userUpdated, setUserUpdated] = React.useState(true);
    const [user, setUser] = React.useState({ twoFactorEnabled: false } as AccountStore.Account);
    const [showUserUpdatedMessage, setShowUserUpdatedMessage] = React.useState(false);
    const [sharedKey, setSharedKey] = React.useState("");
    const [authenticatorUri, setAuthenticatorUri] = React.useState("");
    const [humanJson, setHumanJson] = React.useState("");

    const [loginLink, setLoginLink] = React.useState("");

    const [otpChallengeId, setOtpChallengeId] = React.useState("");
    const [otpPurpose, setOtpPurpose] = React.useState("Activation");
    const [otpCode, setOtpCode] = React.useState("");
    const [otpResult, setOtpResult] = React.useState(null as any);
    const [otpError, setOtpError] = React.useState("");

    const navigate = useNavigate();

    React.useEffect(() => {
        GetCurrentUser();
        axios
            .get("/Account/LoginLink", {
                params: {
                    link: window.location.origin + "/" + "profile",
                },
            })
            .then((response) => {
                setLoginLink(response.data);
            });
    }, []);

    React.useEffect(() => {
        if (!userUpdated) {
            debouncedUpdate(user);
        }
    }, [userUpdated, user]);

    const debouncedUpdate = React.useRef(
        lodash.debounce((user) => {
            return AccountStore.UpdateUser(user).then((response) => {
                GetCurrentUser();
                setShowUserUpdatedMessage(true);
                setUserUpdated(true);

                setTimeout(() => {
                    setShowUserUpdatedMessage(false);
                }, 1500);
                return response;
            });
        }, 1500)
    ).current;

    const GetCurrentUser = () => {
        AccountStore.GetCurrentLoggedInUser(true).then((user) => {
            setCurrentUser(user);
            setUser(lodash.clone(user));
            setHumanJson(HumanJson.format(user.data || {}));
        });
    };

    const handleProfileDataChange = (key) => (e) => {
        let value = e.target.value;

        let userClone = lodash.clone(user);

        lodash.set(userClone, key, value);
        setUserUpdated(false);
        setUser(userClone);
    };

    const resendEmailConfirmation = () => {
        axios
            .get("/api/AccountActions/ResendAccountConfirmation", {
                params: {
                    email: currentUser.email,
                },
            })
            .then((response) => {
                setShowResendConfirmationModal(true);
            });
    };

    const toggleResendConfirmation = () => {
        setShowResendConfirmationModal(!showResendConfirmationModal);
    };

    const updateUser = () => {
        AccountStore.UpdateUser(user).then((response) => {
            navigate("/");
        });
    };

    const copyLink = (url) => () => {
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard.writeText(url).then(
                    function () {
                        /* clipboard successfully set */
                    },
                    function (e) {
                        console.log(e);
                        /* clipboard write failed */
                    }
                );
            }
        });
    };

    const sendTestOtp = () => {
        setOtpError("");
        setOtpResult(null);
        AccountStore.TestRequestOtp(currentUser.id, currentUser.phoneNumber, otpPurpose)
            .then((response) => {
                setOtpChallengeId(response.challengeId);
                setOtpResult(response);
            })
            .catch((e) => {
                setOtpError(e.response?.data?.message || "Failed to request OTP");
            });
    };

    const verifyTestOtp = () => {
        setOtpError("");
        AccountStore.TestVerifyOtp(otpChallengeId, otpCode, otpPurpose)
            .then((response) => {
                setOtpResult(response);
            })
            .catch((e) => {
                setOtpError(e.response?.data?.message || "Failed to verify OTP");
            });
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center gap-4 mb-4">
                    <Avatar round={true} size="70" name={currentUser.fullName} src={currentUser.photo} />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {currentUser.firstName} {currentUser.lastName}
                        </h1>
                        <h2 className="text-xl text-gray-600">{currentUser.email}</h2>
                        <p className="text-sm text-gray-500">{currentUser.userName}</p>
                    </div>
                </div>

                <button
                    onClick={copyLink(loginLink)}
                    className="inline-flex items-center text-blue-600 hover:text-blue-500 text-sm font-medium"
                >
                    <FontAwesomeIcon icon={["fad", "link"]} className="mr-2" />
                    Copy Login Link
                </button>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <nav className="flex space-x-4 px-6 py-3 border-b border-gray-200">
                    <Link
                        to="/profile"
                        className="px-3 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 border-b-2 border-blue-600"
                    >
                        <FontAwesomeIcon icon={["fad", "user"]} className="mr-2" />
                        Profile
                    </Link>
                    <Link
                        to="/profile/data"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "database"]} className="mr-2" />
                        Data
                    </Link>
                    <Link
                        to="/profile/mfa"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-2" />
                        MFA
                    </Link>
                    <Link
                        to="/profile/security"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "lock"]} className="mr-2" />
                        Security
                    </Link>
                    <Link
                        to="/profile/notifications"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "bell"]} className="mr-2" />
                        Notifications
                    </Link>
                </nav>
            </div>

            {/* Email Confirmation Warning */}
            {currentUser.email && !currentUser.emailConfirmed && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-md">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-yellow-400 text-xl" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                Your email address has not been confirmed.{" "}
                                <button
                                    onClick={resendEmailConfirmation}
                                    className="font-medium underline hover:text-yellow-600"
                                >
                                    Click here to resend email confirmation
                                </button>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                    <FormGroup>
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                            id="firstName"
                            type="text"
                            disabled
                            value={user.firstName || ""}
                            onChange={handleProfileDataChange("firstName")}
                            className={!user.firstName ? "border-red-300" : ""}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                            id="lastName"
                            type="text"
                            disabled
                            value={user.lastName || ""}
                            onChange={handleProfileDataChange("lastName")}
                            className={!user.lastName ? "border-red-300" : ""}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="cellNumber">Cell Number</Label>
                        <Input
                            id="cellNumber"
                            type="text"
                            disabled
                            value={user.phoneNumber || ""}
                            onChange={handleProfileDataChange("phoneNumber")}
                            className={!user.phoneNumber ? "border-red-300" : ""}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="text"
                            disabled
                            value={user.email || ""}
                            onChange={handleProfileDataChange("email")}
                            className={!user.email ? "border-red-300" : ""}
                        />
                    </FormGroup>
                </div>
            </div>

            {/* OTP Test Harness (Admin only) */}
            {currentUser.isAdmin && currentUser.isAdmin() && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        <FontAwesomeIcon icon={["fad", "mobile-notch"]} className="mr-2" />
                        OTP Test Harness
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                        Sends a real OTP to your own phone number ({currentUser.phoneNumber || "no phone number on file"}) using the
                        same requestotp/verifyotp process used in production, so you can validate the end-to-end flow.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl items-end">
                        <FormGroup>
                            <Label htmlFor="otpPurpose">Purpose</Label>
                            <select
                                id="otpPurpose"
                                className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                                value={otpPurpose}
                                onChange={(e) => setOtpPurpose(e.target.value)}
                            >
                                <option value="Activation">Activation</option>
                                <option value="Redemption">Redemption</option>
                            </select>
                        </FormGroup>
                        <Button variant="secondary" onClick={sendTestOtp} disabled={!currentUser.phoneNumber}>
                            Send Test OTP
                        </Button>
                    </div>

                    {otpChallengeId && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl items-end mt-4">
                            <FormGroup>
                                <Label htmlFor="otpCode">Code Received</Label>
                                <Input id="otpCode" type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} />
                            </FormGroup>
                            <Button variant="primary" onClick={verifyTestOtp} disabled={!otpCode}>
                                Verify Code
                            </Button>
                        </div>
                    )}

                    {otpError && (
                        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-700">{otpError}</p>
                        </div>
                    )}

                    {otpResult && (
                        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(otpResult, null, 2)}</pre>
                        </div>
                    )}
                </div>
            )}

            {/* Success Message */}
            {showUserUpdatedMessage && (
                <div className="fixed bottom-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-500 mr-2" />
                        <p className="text-sm font-medium text-green-800">Profile updated successfully</p>
                    </div>
                </div>
            )}

            {/* Resend Confirmation Modal */}
            <Modal
                isOpen={showResendConfirmationModal}
                onClose={toggleResendConfirmation}
                title="Email Confirmation Sent"
                icon={["fad", "envelope"]}
            >
                <div className="p-6">
                    <p className="text-gray-700">
                        Email confirmation request resent. Please check your inbox and junk folders.
                    </p>
                </div>
                <div className="flex justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <Button variant="primary" onClick={toggleResendConfirmation}>
                        Close
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default Profile;
